import { useTranslation } from "react-i18next"
import { Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { ColorItem } from "components/ColorItem/ColorItem"
import { Errors } from "./Errors"

type ColorObj = {
  id?: string
  color: string
  sku: string
  name: string
  upc?: string
  skuId?: string
  externalId?: string
}

interface ColorRowErrors {
  name?: string[]
  color?: string[]
}

interface MakeupVariantsCardProps {
  colors: ColorObj[]
  rowErrors?: Array<ColorRowErrors | undefined>
  globalErrors?: string[]
  canAddRemove: boolean
  onChange: (id: string, key: "name" | "color", value: string) => void
  onRemove: (id: string) => void
  onAdd: () => void
}

export const MakeupVariantsCard = ({
  colors,
  rowErrors,
  globalErrors,
  canAddRemove,
  onChange,
  onRemove,
  onAdd,
}: MakeupVariantsCardProps) => {
  const { t } = useTranslation()
  const count = colors.length
  const canRemoveVariant = canAddRemove && count > 1
  const countLabel =
    count === 1 ? t("productPage.variants.countOne") : t("productPage.variants.count", { count })

  return (
    <div className="space-y-2" data-testid="makeup-variants-card">
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <Table className="min-w-[560px] table-fixed" data-testid="makeup-variants-table">
            <colgroup>
              <col />
              <col className="w-[220px]" />
              <col className="w-[80px]" />
            </colgroup>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead className="pl-4 text-xs font-medium text-foreground">
                  {countLabel}
                </TableHead>
                <TableHead className="text-xs font-medium text-foreground">
                  {t("productPage.variants.color")}
                </TableHead>
                <TableHead className="pr-4 text-right text-xs font-medium text-foreground">
                  {t("productPage.variants.action")}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {colors.length === 0 ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell
                    colSpan={3}
                    className="py-10 text-center text-sm text-muted-foreground"
                  >
                    {t("productPage.variants.empty")}
                  </TableCell>
                </TableRow>
              ) : (
                colors.map((colorObj, index) => {
                  const rowErr = rowErrors?.[index]
                  const combinedRowErrors = [...(rowErr?.name ?? []), ...(rowErr?.color ?? [])]
                  return (
                    <TableRow
                      key={colorObj.id}
                      data-testid="makeup-variant-row"
                      data-variant-id={colorObj.id}
                    >
                      <TableCell className="pl-4 align-top py-3">
                        <Input
                          data-testid="makeup-variant-name-input"
                          value={colorObj.name}
                          onChange={(event) => onChange(colorObj.id!, "name", event.target.value)}
                          placeholder={t("productPage.variants.variantNamePlaceholder")}
                          className={cn(
                            "h-9 border-transparent bg-transparent px-2 shadow-none focus-visible:border-input focus-visible:bg-background",
                            rowErr?.name?.length && "border-destructive/40"
                          )}
                        />
                        {combinedRowErrors.length > 0 ? (
                          <div className="mt-0.5 px-2">
                            <Errors _errors={combinedRowErrors} />
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="align-top py-3">
                        <div className="flex h-9 items-center gap-2">
                          <ColorItem
                            value={colorObj.color}
                            onChange={(value) => onChange(colorObj.id!, "color", value)}
                          />
                          <span className="font-mono text-xs text-muted-foreground">
                            {colorObj.color || "—"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="pr-4 text-right align-top py-3">
                        {canRemoveVariant ? (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-muted-foreground hover:text-destructive"
                            onClick={() => onRemove(colorObj.id!)}
                            aria-label="Remove variant"
                            data-testid="makeup-variant-remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      {canAddRemove ? (
        <div className="flex items-start justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5 px-2.5 [&_svg]:size-3.5"
            onClick={onAdd}
            data-testid="makeup-variant-add"
          >
            <Plus />
            {t("productPage.variants.addNew")}
          </Button>
          {globalErrors && globalErrors.length > 0 ? <Errors _errors={globalErrors} /> : null}
        </div>
      ) : null}
    </div>
  )
}
